import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useKeyPress } from '@node-space/hooks'
import { Box, Button, Dialog, DialogProps } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { getPasswordSecret, verify2FACode } from 'services/ApiKeysService'
import Enable2FANoticeModal from './TwoFAStepNotice'
import Enable2FAPasswordModal from './TwoFAStepPassword'
import Enable2FAVerifyModal from './TwoFAStepVerify'

interface TwoFAModalProps {
  notice?: {
    navTitle: string
    heading: string
    description: string
  }
  onComplete?: () => void
}

const TwoFAModal = ({ notice, onComplete }: TwoFAModalProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled, showTwoFAModal, setShowTwoFAModal } = useTwoFAContext()

  const [isLoading, setIsLoading] = useState(false)
  const [secret, setSecret] = useState('')
  const [password, setPassword] = useState('')
  const [hasPasswordError, setHasPasswordError] = useState(false)
  const [otc, setOtc] = useState('')
  const [hasOtcError, setHasOtcError] = useState(false)
  const [currentStep, setCurrentStep] = useState(notice ? 0 : 1)
  const startStep = notice ? 0 : 1

  const resetSteps = () => {
    setShowTwoFAModal(false)
    setCurrentStep(startStep)
    setPassword('')
    setHasPasswordError(false)
    setHasOtcError(false)
  }

  const handleKeyPress = useCallback(() => {
    if (!showTwoFAModal) {
      return
    }
    setShowTwoFAModal(false)
  }, [showTwoFAModal])

  useKeyPress('Escape', handleKeyPress)

  const onPasswordSubmitClicked = async () => {
    try {
      setHasPasswordError(false)
      setIsLoading(true)
      const res = await getPasswordSecret(password)
      setIsLoading(false)
      if (res?.body) {
        setSecret(res?.body)
        // TODO: implement logic for disabling 2FA
        twoFactorEnabled ? setShowTwoFAModal(false) : setCurrentStep(step => step + 1)
      } else {
        setHasPasswordError(true)
      }
    } catch (err) {
      setIsLoading(false)
      logSentryError('Error from TwoFAModal - onPasswordSubmitClicked', err)
    }
  }

  const onVerify = async () => {
    try {
      setHasOtcError(false)
      setIsLoading(true)
      await verify2FACode(otc)
      setIsLoading(false)
      onSuccessVerify()
    } catch (err) {
      if (err.status === 412) {
        setHasOtcError(true)
      } else {
        logSentryError('Error from TwoFAModal - onVerify', err)
      }
      setIsLoading(false)
    }
  }

  const onSuccessVerify = useCallback(() => {
    resetSteps()
    onComplete()
  }, [resetSteps, onComplete])

  const steps: Array<{ actions: DialogProps['ActionButtons'] }> = [
    // notice step
    {
      actions: [
        <Button
          key="submit"
          data-testid={twoFactorEnabled ? 'notify-disable-2FA' : 'notify-enable-2FA'}
          variant="primary"
          onClick={() => setCurrentStep(1)}
        >
          {twoFactorEnabled ? t('2FADisable') : t('2FAEnable')}
        </Button>,
      ],
    },
    // password step
    {
      actions: [
        <Button
          key="submit"
          data-testid="submit-2FA-password"
          loading={isLoading}
          variant="primary"
          disabled={password.length === 0 || isLoading}
          onClick={onPasswordSubmitClicked}
        >
          {t('submit')}
        </Button>,
        <Button
          key="cancel"
          data-testid="cancel-2FA"
          variant="secondary"
          disabled={isLoading}
          onClick={resetSteps}
        >
          {t('cancel')}
        </Button>,
      ],
    },
    // qr code step
    {
      actions: [
        <Button
          key="submit"
          loading={isLoading}
          data-testid="verify-2FA-otc"
          variant="primary"
          disabled={otc.length !== 6 || isLoading}
          onClick={onVerify}
        >
          {t('verify')}
        </Button>,
      ],
    },
  ]

  const actions = steps[currentStep].actions

  const getStep = () => {
    switch (currentStep) {
      case 0:
        return <Enable2FANoticeModal heading={notice?.heading} description={notice?.description} />
      case 1:
        return (
          <Enable2FAPasswordModal
            password={password}
            isLoading={isLoading}
            hasError={hasPasswordError}
            onChange={val => {
              setPassword(val)
              setHasPasswordError(false)
            }}
          />
        )
      default:
        return (
          <Enable2FAVerifyModal
            secret={secret}
            isLoading={isLoading}
            hasError={hasOtcError}
            onChange={val => {
              setOtc(val)
              setHasOtcError(false)
            }}
          />
        )
    }
  }

  return (
    <Dialog
      open={showTwoFAModal}
      onClose={() => setShowTwoFAModal(false)}
      ActionButtons={actions}
      type="utility"
      title={
        currentStep === 0 && notice
          ? notice.navTitle
          : twoFactorEnabled
            ? t('disable2FAAuthentication')
            : t('enable2FAAuthentication')
      }
    >
      <Box>{getStep()}</Box>
    </Dialog>
  )
}

export default TwoFAModal
